import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Users, X } from 'lucide-react';
import {
  formatIqd,
  fromMillidinars,
  type CustomerListResponse,
  type DayPassDto,
  type DeviceDto,
  type SessionDto,
} from '@gc/shared';

import { api, ApiError } from '@/lib/api';
import { useAuth } from '@/features/auth/useAuth';
import { OpenTabPicker } from '@/features/tabs/OpenTabPicker';
import { cardAmountMd, isLiveTab } from '@/features/tabs/liveTabs';
import {
  buildJoinTabPayload,
  buildStartSessionPayload,
  findPassForParty,
  identityTimePercent,
  passToCarry,
  previewSessionPrice,
  reservationUsesAccountBalance,
  type SelectedCustomer,
  type SittingIdentity,
} from './sessionLogic';

/*
 * No payment-method row and no "Cashier Print" switch (audit, 2026-09-23).
 *
 * Both were on this panel and neither reached the server: the Cash / Qi Card /
 * Card / Visitor buttons and the print toggle only ever restyled themselves,
 * and nothing a start sends carried either. Staff could press Cash or switch
 * printing off and nothing changed. Payment is taken at the till, with the
 * total it is paying (see the note on discounts below) — so the controls went,
 * rather than being wired to a decision this screen does not make.
 */

interface ReservationDrawerProps {
  device: DeviceDto;
  /**
   * Every machine this sitting covers, when staff ticked several and pressed
   * Reserve. Defaults to just `device`. They all open on ONE tab: five friends
   * arriving together are one party with one bill, which is the case this
   * comes up in. Prices and round type still come from `device` — the first of
   * the selection — because the panel quotes one rate.
   */
  party?: readonly DeviceDto[];
  now: number;
  currencySymbol: string;
  onClose: () => void;
  onSaved: () => void;
}

/**
 * The panel behind a device tile (SPEC §5.3).
 *
 * A FREE machine gets the fields that open it for play: who is on it, whether
 * they are joining a tab, free mode, and an optional planned duration. Pressing
 * Save on the defaults is the walk-in case — a visitor named after the machine,
 * no account, nothing deposited.
 *
 * A BUSY machine gets the same panel READ-ONLY: the tab, the clock, what it has
 * run up so far. It no longer settles anything. Freeing a machine is the tile's
 * "Stop & close" (and the menu's "End reservation"); taking the money is the
 * Outstanding screen, where a cashier can find a party by name and settle just
 * the part of it that is leaving. A panel that both starts sittings and takes
 * payment put those two a mis-tap apart on the busiest screen in the building.
 *
 * **No discount field here, by the owner's decision.** A discount is a
 * concession made when someone pays, so it belongs at the till with the total
 * it is coming off — not on the screen that opens a machine, hours earlier,
 * against a bill nobody has added up yet.
 *
 * **Reserving never starts free mode.** There is no checkbox here and nothing
 * inherited from the device: a reservation is a paid sitting in the portal, and
 * the board says "Reserved" over it. Free mode has one entry point, the Play
 * button, so the word on a tile always names something somebody chose.
 *
 * The price preview runs through the shared `computeBucketDiscounts` (via
 * `sessionLogic.ts`) — the same function checkout charges with — fed by the
 * registered customer's own time percent.
 */
export function ReservationDrawer({
  device,
  party,
  now,
  currencySymbol,
  onClose,
  onSaved,
}: ReservationDrawerProps) {
  const { t } = useTranslation();
  const machines = party && party.length > 0 ? party : [device];
  const { can } = useAuth();
  /*
   * ACCOUNTS — AND THEREFORE BALANCES — ARE A PC THING.
   *
   * A PC sitting runs against the customer's account: the portal counts their
   * balance down, and the sweep ends the sitting when it runs out. Everything
   * else in the venue is reserved under a NAME and its clock counts UP, settled
   * at the counter when they finish (owner: "it has nothing to do with account
   * balance").
   *
   * Attaching an account to a PS5, a billiard table, a table or the cinema is
   * what dragged balance into sittings that have no business with it — the tile
   * started counting DOWN against money the customer was never going to spend
   * that way. So the member search is simply not offered there: what cannot be
   * attached cannot start behaving like a PC.
   *
   * Keyed on the device TYPE, per the owner: anything that is not a PC. The
   * quote comes from `device` for the same reason prices and round type do — it
   * is the machine this panel is describing.
   */
  const canSearchCustomers =
    can('customers.manage') && reservationUsesAccountBalance(device.deviceType);
  const session = device.currentSession;
  const isRunning = Boolean(session);
  const isRoundBased = device.reservationType === 'ROUND';

  const [durationMinutes, setDurationMinutes] = useState('');
  const [pickedRoundType, setRoundType] = useState<'SINGLE' | 'DOUBLE' | 'THREE_BALL'>('SINGLE');
  // 3-ball only where the device prices it — the stop dialog and the till
  // follow the same rule. Offering it on a table without a 3-ball price quoted
  // those rounds at 0 here and billed them at 0 when the sitting ended.
  const offersThreeBall = device.threeBallPriceMd != null;
  const roundType =
    pickedRoundType === 'THREE_BALL' && !offersThreeBall ? 'SINGLE' : pickedRoundType;
  const [rounds, setRounds] = useState('1');
  /**
   * Who this sitting is for — ONE of a walk-in name, an account, or an open
   * tab. See `SittingIdentity`: holding all three at once is what let a chosen
   * tab be silently dropped on the way to the server.
   */
  const [identity, setIdentity] = useState<SittingIdentity>({
    kind: 'walkIn',
    name: session?.tabName ?? device.name,
  });
  const [tabPickerOpen, setTabPickerOpen] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerOptions, setCustomerOptions] = useState<SelectedCustomer[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [dayPasses, setDayPasses] = useState<DayPassDto[]>([]);

  const customer = identity.kind === 'customer' ? identity.customer : null;

  /**
   * Today's Boot Camp passes, so a party that bought one can be seated on it.
   *
   * Read once when the drawer opens rather than per identity change: the list
   * is one business day of passes, and re-fetching on every keystroke of the
   * customer search would be a request per letter. Failure is silent on
   * purpose — a venue that never sells passes, or a cashier without the
   * permission, must still be able to start an ordinary sitting.
   */
  useEffect(() => {
    if (isRunning) return;
    let cancelled = false;
    void api
      .get<{ passes: DayPassDto[] }>('/day-passes')
      .then((screen) => {
        if (!cancelled) setDayPasses(screen.passes);
      })
      .catch(() => {
        if (!cancelled) setDayPasses([]);
      });
    return () => {
      cancelled = true;
    };
  }, [isRunning]);

  /** The pass this party holds, if any. Null for a walk-in name — see the helper. */
  const partyPass = useMemo(() => findPassForParty(dayPasses, identity), [dayPasses, identity]);
  /**
   * The pass staff said NO to, by id. Keyed on the pass rather than a plain
   * flag so switching to another party's tab offers that party's pass afresh
   * instead of inheriting a "no" said about somebody else.
   */
  const [declinedPassId, setDeclinedPassId] = useState<string | null>(null);
  const usePass = partyPass !== null && declinedPassId !== partyPass.id;

  // Debounced registered-customer search (start flow only). Wired only when
  // the permission exists — the server would refuse `/customers` anyway, so
  // the drawer says nothing instead of failing quietly.
  useEffect(() => {
    if (!canSearchCustomers || isRunning || identity.kind !== 'walkIn') return;
    const query = customerSearch.trim();
    if (!query) {
      setCustomerOptions([]);
      return;
    }
    const timer = setTimeout(() => {
      void (async () => {
        try {
          const response = await api.get<CustomerListResponse>(
            `/customers?search=${encodeURIComponent(query)}&pageSize=8`,
          );
          setCustomerOptions(
            response.rows.map(({ id, fullName, discountTimePercent }) => ({
              id,
              fullName,
              discountTimePercent,
            })),
          );
        } catch {
          setCustomerOptions([]);
        }
      })();
    }, 300);
    return () => clearTimeout(timer);
  }, [canSearchCustomers, isRunning, identity.kind, customerSearch]);

  const hourlyMd = fromMillidinars(device.hourlyPriceMd);
  const roundPriceMd = fromMillidinars(
    roundType === 'DOUBLE'
      ? device.doublePriceMd
      : roundType === 'THREE_BALL'
        ? (device.threeBallPriceMd ?? '0')
        : device.singleGamePriceMd,
  );

  /**
   * Price preview. The server recomputes authoritatively on save — this only
   * shows the employee what they are about to charge.
   */
  const preview = useMemo(() => {
    const baseMd = isRunning
      ? fromMillidinars(session!.runningCostMd)
      : isRoundBased
        ? fromMillidinars(BigInt(roundPriceMd) * BigInt(Number(rounds) || 0))
        : fromMillidinars(
            (BigInt(hourlyMd) * BigInt(Math.round((Number(durationMinutes) || 0) * 1000))) /
              60_000n,
          );

    return previewSessionPrice({
      baseMd,
      // A Boot Camp pass is the only thing here that makes time cost nothing:
      // the server snapshots a zero rate because the customer already paid at
      // the counter. Free mode does NOT — it is a screen mode, billed normally
      // — and quoting zero for it told staff a machine was free that the till
      // would then charge for. Only while the pass is being used: a declined
      // pass leaves the sitting billed like any other.
      unbilledTime: !isRunning && usePass,
      customerTimePercent: isRunning ? 0 : identityTimePercent(identity),
      // Always zero: discounts are the till's, at the moment of paying.
      manualDiscountIqd: 0,
    });
  }, [
    isRunning,
    session,
    isRoundBased,
    roundPriceMd,
    rounds,
    hourlyMd,
    durationMinutes,
    identity,
    usePass,
  ]);

  /**
   * Start only. A running sitting has no save here — this panel stopped being
   * the place a session ends, so there is nothing for it to submit.
   *
   * With several machines, they all land on ONE tab. The first one to start
   * successfully creates it and every later machine joins by id; if that first
   * attempt is refused (someone got to the PC first), the next one becomes the
   * tab-maker rather than the whole party failing behind it. Sequential because
   * the tab does not exist until a machine has made it.
   */
  async function handleSave(event: FormEvent) {
    event.preventDefault();
    if (isRunning) return;
    setError(null);
    setSubmitting(true);

    const refusals: string[] = [];
    let partyTabId: string | null = identity.kind === 'tab' ? identity.tab.id : null;
    let started = 0;
    // Whether an earlier machine in this save already started on the pass —
    // one pass is one seat, so every machine after it is charged as usual.
    let passSeated = false;

    try {
      for (const machine of machines) {
        const dayPassId = passToCarry({ partyPass, usePass, alreadySeated: passSeated });
        // Once the party has a tab, later machines JOIN it rather than
        // repeating the name and opening tabs of their own.
        const payload = partyTabId
          ? buildJoinTabPayload({
              deviceId: machine.id,
              tabId: partyTabId,
              durationMinutes,
              // THE DEVICE FLAG DECIDES (owner reversal, 2026-08-11): a PC
              // whose Edit Device has free mode ON hands the Windows desktop
              // over on EVERY sitting — staff Reserve and a customer's own
              // portal sign-in alike — and the customer can call the portal
              // back with Ctrl+Shift+P. This replaced "RESERVING IS NEVER
              // FREE MODE": that rule existed because the board's tile used
              // to read "Free mode" in green over these sittings, and now the
              // tile calls them Reserved like any other paid machine.
              freeMode: machine.freeModeEnabled,
              dayPassId,
            })
          : buildStartSessionPayload({
              deviceId: machine.id,
              identity,
              fallbackName: machine.name,
              durationMinutes,
              // THE DEVICE FLAG DECIDES — see the join branch above.
              freeMode: machine.freeModeEnabled,
              dayPassId,
            });

        try {
          const session = await api.post<SessionDto>('/sessions', payload);
          started += 1;
          partyTabId ??= session.tabId;
          if (dayPassId) passSeated = true;
        } catch (caught) {
          refusals.push(
            `${machine.name}: ${caught instanceof ApiError ? caught.message : t('sessions.saveFailed')}`,
          );
        }
      }

      // Anything that started is real and the board must show it, even when a
      // sibling was refused — so reload either way and only hold the panel
      // open when there is something staff still need to read.
      if (started > 0) onSaved();
      if (refusals.length === 0) {
        onClose();
        return;
      }
      setError(refusals.join('\n'));
    } finally {
      setSubmitting(false);
    }
  }

  const fieldClass =
    'w-full rounded-control border border-subtle bg-input px-3 py-2 text-sm text-content outline-none placeholder:text-content-muted focus:border-primary';

  const elapsedLabel = session
    ? (() => {
        const mins = Math.max(
          0,
          Math.floor((now - new Date(session.startedAt).getTime()) / 60_000),
        );
        return `${Math.floor(mins / 60)}:${String(mins % 60).padStart(2, '0')}`;
      })()
    : null;

  const joinedTabAmountMd = identity.kind === 'tab' ? cardAmountMd(identity.tab) : '0';

  return (
    <>
      {/* Scrim. Clicking away closes without saving. */}
      <div
        className="fixed inset-0 z-40 bg-black/50"
        onClick={onClose}
        role="presentation"
        aria-hidden="true"
      />

      {/* Drawer pinned to the inline-end edge, so it flips sides under RTL. */}
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={t('sessions.reserveTitle', { device: device.name })}
        className="fixed inset-y-0 z-50 flex w-full max-w-md flex-col border-s border-subtle bg-ground shadow-2xl"
        style={{ insetInlineEnd: 0 }}
      >
        <header className="flex items-center gap-3 border-b border-subtle p-4">
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.cancel')}
            className="rounded-control p-1 text-content-secondary hover:bg-hover hover:text-content"
          >
            <X className="h-5 w-5" aria-hidden />
          </button>
          <h2 className="flex-1 truncate text-base font-semibold text-content">
            {machines.length > 1
              ? t('sessions.reservationOfMany', { count: machines.length })
              : t('sessions.reservationOf', { device: device.name })}
          </h2>
        </header>

        <form
          onSubmit={(event) => void handleSave(event)}
          className="flex min-h-0 flex-1 flex-col overflow-auto p-4"
        >
          {machines.length > 1 ? (
            // Which machines this covers, and that they share one bill — the
            // difference between five friends and five strangers is the whole
            // reason someone ticked five boxes.
            <div className="mb-4 rounded-control bg-raised px-4 py-3">
              <p data-party-devices className="text-sm text-content">
                {machines.map((machine) => machine.name).join(' · ')}
              </p>
              <p className="mt-1 text-xs text-content-secondary">{t('sessions.onePartyOneTab')}</p>
            </div>
          ) : null}

          {!isRunning && partyPass ? (
            // STATED, never silent. Seating on a pass makes the sitting bill
            // nothing, and a zero that appears with no explanation is the kind
            // of thing staff correct by hand at the till — charging a customer
            // twice for the same hours.
            <div
              data-day-pass={partyPass.id}
              className="mb-4 rounded-control border border-[var(--brand-primary)] bg-raised px-4 py-3"
            >
              <p className="text-sm font-semibold text-content">
                {t('sessions.dayPass.title', { name: partyPass.offerName })}
              </p>
              <p className="mt-1 text-xs text-content-secondary">
                {usePass
                  ? t('sessions.dayPass.body', {
                      until: new Date(partyPass.validUntil).toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit',
                      }),
                    })
                  : t('deviceBoard.drawer.dayPass.declined')}
              </p>
              {machines.length > 1 ? (
                // One pass is one seat: say so where five machines are about
                // to open, so nobody expects the other four to be free too.
                <p className="mt-1 text-xs text-content-secondary">
                  {t('deviceBoard.drawer.dayPass.oneSeat')}
                </p>
              ) : null}
              {/* The pass is OFFERED, never forced: staff can say no — the
                  customer may be saving it, or it may belong to somebody else
                  on the same tab. */}
              <label className="mt-2 flex items-center gap-2 text-sm text-content">
                <input
                  type="checkbox"
                  data-day-pass-use
                  checked={usePass}
                  onChange={(event) =>
                    setDeclinedPassId(event.target.checked ? null : partyPass.id)
                  }
                  className="h-4 w-4 accent-[var(--brand-primary)]"
                />
                {machines.length > 1
                  ? t('deviceBoard.drawer.dayPass.useFor', { device: machines[0]!.name })
                  : t('deviceBoard.drawer.dayPass.use')}
              </label>
            </div>
          ) : null}

          {/*
            The free-mode checkbox that used to sit here is gone.

            It arrived pre-ticked from the device's own flag, so reserving a PC
            the venue had set to free mode started a free-mode sitting whether
            or not anyone read the box — and the board then flew "Free mode"
            over an ordinary paid reservation. Free mode has one deliberate
            entry point now, the Play button, and this screen reserves.
          */}

          <div className="mb-4 grid grid-cols-3 gap-3">
            <Field label={t('sessions.duration')}>
              {/*
                step="1", NOT a coarser step. With min="1" a step of 15 makes
                the valid values 1, 16, 31, 46, 61 — so typing 60, the most
                obvious duration there is, silently blocks the form with no
                visible error. Any whole minute must be accepted.
              */}
              <input
                type="number"
                min="1"
                step="1"
                value={durationMinutes}
                onChange={(event) => setDurationMinutes(event.target.value)}
                placeholder={t('sessions.durationPlaceholder')}
                disabled={isRunning}
                className={`${fieldClass} tabular`}
              />
            </Field>

            <Field label={t('sessions.roundType')}>
              <select
                value={roundType}
                onChange={(event) => setRoundType(event.target.value as typeof roundType)}
                disabled={!isRoundBased}
                className={fieldClass}
              >
                <option value="SINGLE">{t('sessions.single')}</option>
                <option value="DOUBLE">{t('sessions.double')}</option>
                {offersThreeBall ? (
                  <option value="THREE_BALL">{t('sessions.threeBall')}</option>
                ) : null}
              </select>
            </Field>

            <Field label={t('sessions.hourlyPrice')}>
              <input
                readOnly
                value={formatIqd(hourlyMd, { currencySymbol })}
                className={`${fieldClass} tabular text-content-secondary`}
              />
            </Field>
          </div>

          {isRoundBased ? (
            <div className="mb-4 grid grid-cols-3 gap-3">
              <Field label={t('sessions.roundsPlayed')}>
                {/* Read-only once play has started: the rounds that decide the
                    bill are counted when the reservation ENDS, in the stop
                    dialog, not guessed at from here mid-game. */}
                <input
                  type="number"
                  min="0"
                  value={rounds}
                  onChange={(event) => setRounds(event.target.value)}
                  disabled={isRunning}
                  className={`${fieldClass} tabular`}
                />
              </Field>
              <Field label={t('sessions.roundPrice')}>
                <input
                  readOnly
                  value={formatIqd(roundPriceMd, { currencySymbol })}
                  className={`${fieldClass} tabular text-content-secondary`}
                />
              </Field>
              {elapsedLabel ? (
                <Field label={t('sessions.elapsed')}>
                  <input readOnly value={elapsedLabel} className={`${fieldClass} tabular`} />
                </Field>
              ) : null}
            </div>
          ) : null}

          {/* Price and total only. The discount that used to sit between them
              moved to the till — see this file's header. */}
          <div className="mb-4 grid grid-cols-2 gap-3">
            <Field label={t('sessions.price')}>
              <input
                readOnly
                value={formatIqd(preview.baseMd, { currencySymbol })}
                className={`${fieldClass} tabular text-content-secondary`}
              />
            </Field>
            <Field label={isRunning ? t('sessions.runningTotal') : t('sessions.totalPrice')}>
              <input
                readOnly
                value={formatIqd(preview.totalMd, { currencySymbol })}
                className={`${fieldClass} tabular font-semibold text-content`}
              />
            </Field>
          </div>

          {/* The customer-discount line that sat here is gone with the cashback
              change: the hourly rate is charged in full, and the percent is a
              refill cashback rate, not a price cut to promise. */}

          {identity.kind === 'tab' ? (
            // Joined to a party. Its name, machines and money, so staff can see
            // at a glance they picked the right one — the same figure, in the
            // same words, as the card they just tapped. A live card says what
            // the party OWES, orders included; echoing the bare sittings total
            // here showed "0 devices" under a party the card said owed 4,826.
            <Field label={t('sessions.joinTab')}>
              <div className="flex items-center gap-2">
                <span
                  data-joined-tab={identity.tab.id}
                  className="min-w-0 flex-1 rounded-control bg-raised px-3 py-2 text-sm text-content"
                >
                  <span className="block truncate font-medium">{identity.tab.name}</span>
                  <span className="tabular block truncate text-xs text-content-muted">
                    {t('tabs.picker.deviceCount', { count: identity.tab.deviceCount })}
                    {joinedTabAmountMd !== '0'
                      ? ` · ${
                          isLiveTab(identity.tab)
                            ? t('openTabs.card.owes', {
                                amount: formatIqd(fromMillidinars(joinedTabAmountMd), {
                                  currencySymbol,
                                }),
                              })
                            : formatIqd(fromMillidinars(joinedTabAmountMd), { currencySymbol })
                        }`
                      : ''}
                  </span>
                </span>
                <button
                  type="button"
                  onClick={() => setIdentity({ kind: 'walkIn', name: device.name })}
                  className="text-xs text-content-secondary hover:text-content"
                >
                  {t('common.clear')}
                </button>
              </div>
            </Field>
          ) : identity.kind === 'customer' ? (
            <Field label={t('deviceBoard.drawer.customer')}>
              <div className="flex items-center gap-2">
                <span className="flex-1 truncate rounded-control bg-raised px-3 py-2 text-sm text-content">
                  {identity.customer.fullName}
                </span>
                <button
                  type="button"
                  onClick={() => setIdentity({ kind: 'walkIn', name: device.name })}
                  className="text-xs text-content-secondary hover:text-content"
                >
                  {t('deviceBoard.drawer.changeCustomer')}
                </button>
              </div>
            </Field>
          ) : (
            <>
              <Field label={t('sessions.visitorName')}>
                <div className="relative">
                  <input
                    value={identity.name}
                    onChange={(event) => setIdentity({ kind: 'walkIn', name: event.target.value })}
                    disabled={isRunning}
                    className={`${fieldClass} pe-11`}
                  />
                  {/* A real control now. It was an inert adornment, which is
                      exactly where a venue owner looks for "put this on
                      somebody's tab" — so that is what it does. */}
                  <button
                    type="button"
                    data-open-tab-picker-trigger
                    onClick={() => setTabPickerOpen(true)}
                    disabled={isRunning}
                    aria-label={t('sessions.joinTab')}
                    title={t('sessions.joinTab')}
                    aria-haspopup="dialog"
                    aria-expanded={tabPickerOpen}
                    className="absolute top-1/2 grid h-8 w-8 -translate-y-1/2 place-items-center rounded-control text-content-muted hover:bg-hover hover:text-content disabled:cursor-not-allowed disabled:opacity-40"
                    style={{ insetInlineEnd: '0.25rem' }}
                  >
                    <Users className="h-4 w-4" aria-hidden />
                  </button>
                </div>
              </Field>

              {!isRunning && canSearchCustomers ? (
                <div className="mt-3">
                  <Field label={t('deviceBoard.drawer.attachCustomer')}>
                    <div className="relative">
                      <Search
                        className="pointer-events-none absolute top-3 h-4 w-4 text-content-muted"
                        style={{ insetInlineStart: '0.6rem' }}
                        aria-hidden
                      />
                      <input
                        value={customerSearch}
                        onChange={(event) => setCustomerSearch(event.target.value)}
                        placeholder={t('deviceBoard.drawer.searchCustomer')}
                        className={`${fieldClass} ps-8`}
                      />
                      {customerOptions.length > 0 ? (
                        <ul className="mt-1 overflow-hidden rounded-control border border-subtle bg-raised">
                          {customerOptions.map((option) => (
                            <li key={option.id}>
                              <button
                                type="button"
                                onClick={() => {
                                  setIdentity({ kind: 'customer', customer: option });
                                  setCustomerOptions([]);
                                  setCustomerSearch('');
                                }}
                                className="w-full px-3 py-2 text-start text-sm text-content hover:bg-hover"
                              >
                                {option.fullName}
                              </button>
                            </li>
                          ))}
                        </ul>
                      ) : null}
                    </div>
                  </Field>
                </div>
              ) : null}
            </>
          )}

          {error ? (
            <p role="alert" className="mt-3 text-sm text-danger">
              {error}
            </p>
          ) : null}

          <div className="flex-1" />

          {isRunning ? (
            // Nothing to submit. Saying where the two things staff might want
            // next actually live beats a disabled button they will press anyway.
            <p className="mt-6 rounded-control bg-raised px-4 py-3 text-xs text-content-secondary">
              {t('deviceBoard.drawer.runningHint')}
            </p>
          ) : (
            <button
              type="submit"
              disabled={submitting}
              className="mt-6 w-full rounded-full bg-primary py-3 text-sm font-semibold text-primary-fg transition-colors hover:bg-primary-hover disabled:opacity-60"
            >
              {submitting ? t('common.loading') : t('sessions.startPlay')}
            </button>
          )}
        </form>
      </aside>

      {tabPickerOpen ? (
        // Above the drawer's own z-50 so it is not trapped behind it. The
        // picker fetches its own list — see OpenTabPicker; the drawer keeps no
        // copy of the open tabs to go stale.
        <div
          className="fixed inset-0 z-[60] grid place-items-center bg-black/60 p-4"
          role="dialog"
          aria-modal="true"
          aria-label={t('sessions.joinTab')}
        >
          <div className="w-full max-w-lg rounded-card border border-subtle bg-raised p-5 shadow-2xl">
            <h2 className="text-lg font-semibold text-content">{t('sessions.joinTab')}</h2>
            <p className="mt-1 mb-3 text-sm text-content-secondary">
              {t('sessions.joinTabHint', { device: device.name })}
            </p>

            {/*
              TWO NAMED LISTS, not one mixed one.

              "Here now" is a party already in the building — they are added to
              that party's tab and settle at the counter with everything else.
              "Reserve using account balance" is a registered customer, and the
              sitting then runs against their money: the portal counts it down
              and the expiry sweep ends the sitting when it reaches zero.

              Those are different promises to a customer, so they are different
              lists with the second one saying what it does in its own name. The
              account list appears on a PC ONLY — everything else is reserved
              under a name and has nothing to do with a balance.
            */}
            <OpenTabPicker
              value={identity.kind === 'tab' ? identity.tab.id : null}
              onChange={(_tabId, tab) => {
                setIdentity({ kind: 'tab', tab });
                setTabPickerOpen(false);
              }}
              // EVERY LIVE tab — no "here now" narrowing, and no dead ones.
              //
              // This was filtered to parties present today, and the filter hid
              // the commonest case at the counter: somebody who finished on a
              // PC, has not paid, is standing right there and wants a PS5. Time
              // is not billed until checkout, so he has no live session and no
              // bill; and once the business day rolls over he is not "today"
              // either. Measured against the live venue the filter showed 3
              // names out of 52 open tabs. The system does not know who is in
              // the building, staff do, and there is a search box — owner's
              // call: show all, no guessing.
              //
              // "All" is the live list, not every OPEN tab. He owes money, so
              // he is on it whatever day he played. What it leaves out is a tab
              // that owes nothing, runs nothing and holds no pass: a paid-up
              // party that was never closed. Starting a machine on one of those
              // revived it — the cafe's second 'Yaran staff' (owner,
              // 2026-09-23) — and a returning customer is a new walk-in.
              source="live"
              autoFocus
            />

            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={() => setTabPickerOpen(false)}
                className="rounded-full border border-subtle px-4 py-2 text-sm text-content-secondary hover:bg-hover"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block min-w-0">
      <span className="mb-1 block truncate text-xs text-content-secondary">{label}</span>
      {children}
    </label>
  );
}
